import React, { useMemo } from "react";
import { FiltersTypes } from "@common/types/filters/general";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useUserStatus } from "@hooks/useUserStatus";
import {
    ASSIGNED_TO_CLIENT_OPTION_LABEL,
    UNASSIGNED_OPTION_LABEL,
} from "../Tabs/TaskAssigneeFilter/taskAssigneeFilter.constants";
import { formatFilterLabel } from "./formatFilterLabel";

interface Props {
    filters: FiltersTypes;
}

export const TaskAssigneeFilterLabel: React.FC<Props> = ({ filters }) => {
    const { data: workspaceMembers } = useCurrentWorkspaceMembers();
    // Org-level tasks page lists org members, which may not be workspace members.
    const { data: orgMembers } = useCurrentOrganizationMembers();
    const { data: userStatus } = useUserStatus();
    const currentUser = userStatus?.user;
    const { taskAssigneeIds, isTaskUnassigned, isTaskAssignedToClient } =
        filters;

    const labels = useMemo(() => {
        const namesById = new Map<number, string>(
            currentUser ? [[currentUser.id, currentUser.name]] : [],
        );
        [...(orgMembers ?? []), ...(workspaceMembers ?? [])].forEach((member) =>
            namesById.set(member.user.id, member.user.name),
        );
        const memberLabels = (taskAssigneeIds ?? []).flatMap((id) => {
            const name = namesById.get(id);
            return name ? [name] : [];
        });
        return [
            ...(isTaskUnassigned ? [UNASSIGNED_OPTION_LABEL] : []),
            ...(isTaskAssignedToClient
                ? [ASSIGNED_TO_CLIENT_OPTION_LABEL]
                : []),
            ...memberLabels,
        ];
    }, [
        currentUser,
        workspaceMembers,
        orgMembers,
        taskAssigneeIds,
        isTaskUnassigned,
        isTaskAssignedToClient,
    ]);

    if (labels.length === 0) {
        return null;
    }

    return formatFilterLabel(labels);
};
